import { defineComponent as _defineComponent } from 'vue'
import { twitter } from "vue-twitter";
import { DataTableHeader } from "vuetify";

import AdditionalRoleOffice from "@/components/profile/AdditionalRoleOffice.vue";
import AdditionalRolesRow from "@/components/profile/AdditionalRolesRow.vue";
import ArchivedIcon from "@/components/ArchivedIcon.vue";
import BriefcaseDialog from "@/components/BriefcaseFolderMoveDialog.vue";
import Dialog from "@/components/vuetify-wrappers/NjDialog.vue";
import ExportButton from "@/components/buttons/ExportButton.vue";
import NjBtn from "@/components/vuetify-wrappers/NjButton.vue";
import OfficeAddress from "@/components/profile/OfficeAddress.vue";
import ProfileCard from "@/components/ProfileCard.vue";
import ProfileItem from "@/components/profile/ProfileItem.vue";
import ProfilePanel from "@/components/profile/ProfilePanel.vue";
import ProfileSubModuleCards from "@/components/profile/ProfileSubModuleCards.vue";
import ProfileWrapper from "@/components/profile/ProfileWrapper.vue";
import { ApiService, LogService, SnackbarService } from "@/services";
import {
  MetadataKeys,
  Profile as ProfileObj,
  RedirectResponse,
  VignetteHelpers,
  VignetteRoutes,
} from "@/types";
import { useSessionStore } from "@/pinia/session";
import { useAppStore } from "@/pinia/app";
import { computed, onBeforeMount, ref } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { useViewAnalytics } from "@/composition/useViewAnalytics";
import DownloadWithCustomLogoBtn from "@/components/buttons/DownloadWithCustomLogoBtn.vue";
import ElectionStatus from "@/components/ElectionStatus.vue";
import DjangoRouterLink from "@/components/links/DjangoRouterLink.vue";
import { Chip } from "@/types/nj/Chip";
import HierarchyChip from "@/components/profile/HierarchyChip.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'ProfileDetail',
  setup(__props) {

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const route = useRoute();
const router = useRouter();

// <editor-fold desc="App Store Properties">
const appStore = useAppStore();
// </editor-fold>

// <editor-fold desc="Session Store Properties">
const sessionStore = useSessionStore();
const hasCustomLogo = computed(() => sessionStore.hasCustomLogo);
// </editor-fold>

// <editor-fold desc="Profile Properties">
const profile = ref<ProfileObj | null>(null);
const isLoaded = ref(false);
const modules = computed<number[]>(
  () => profile.value?.subscriptionModules || []
);
const socialLinks = computed<
  { title: string; link: string; display?: string }[]
>(() => {
  const facebook = {
    title: "Facebook",
    link: facebookLink.value,
  };
  const twitter = {
    title: "Twitter",
    link: twitterLink.value,
  };
  const youtube = {
    title: "YouTube",
    link: youtubeLink.value,
  };
  const instagram = {
    title: "Instagram",
    link: instagramLink.value,
    display: instagramHandle.value,
  };
  const linkedIn = {
    title: "LinkedIn",
    link: linkedInLink.value,
  };
  const website = {
    title: "Website",
    link: websiteLink.value,
  };
  return [
    ...(facebookLink.value ? [facebook] : []),
    ...(twitterLink.value ? [twitter] : []),
    ...(youtubeLink.value ? [youtube] : []),
    ...(instagramLink.value ? [instagram] : []),
    ...(linkedInLink.value ? [linkedIn] : []),
    ...(websiteLink.value ? [website] : []),
  ];
});
const facebookLink = computed(() => profile.value?.facebookUrl ?? "");
const getTwitterLinkFromString = (twitterString: string): string => {
  if (twitterString) {
    if (twitterString.includes("http")) {
      return twitterString;
    } else if (twitterString.includes("@")) {
      return `https://twitter.com/${twitterString.replace("@", "")}`;
    } else {
      return `https://twitter.com/${twitterString}`;
    }
  }
  return "";
};
const twitterLink = computed(() => {
  if (profile.value?.twitterUsername) {
    return getTwitterLinkFromString(profile.value.twitterUsername);
  }
  return "";
});
const instagramLink = computed(() =>
  profile.value?.instagramUsername
    ? `https://www.instagram.com/${profile.value.instagramUsername}`
    : ""
);
const instagramHandle = computed(() =>
  profile.value?.instagramUsername ? `@${profile.value.instagramUsername}` : ""
);
const youtubeLink = computed(() => profile.value?.youtube ?? "");
const linkedInLink = computed(() => profile.value?.linkedInUrl ?? "");
const websiteLink = computed(() => profile.value?.websiteUrl ?? "");
const presentationCenterLink = computed(
  () => profile.value?.presentationCenterUrl ?? ""
);
const emailLink = computed(() =>
  profile.value?.email.includes("@")
    ? `mailto:${profile.value.email}`
    : profile.value?.email ?? ""
);
const docxUrl = computed(() => profile.value?.docxUrl ?? "");
const docxUrlWithCustomLogo = computed(
  () => `${profile.value?.docxUrl}?custom_logo=True` ?? ""
);
const pdfUrl = computed(() => profile.value?.pdfUrl ?? "");

/**
 * Computes whether to show the electoral history section.
 * If the electoral history is not empty, returns true.
 * Otherwise, returns true if the first elected date and number of terms are set.
 */
const showElectoralHistory = computed<boolean>(() => {
  const electoralHistory = profile.value?.electoralHistory ?? [];
  return electoralHistory.length > 0 || !!firstElectedWithTenureText.value;
});

/**
 * Computes the text to display for the first elected with tenure.
 * If the first elected date is not set, returns an empty string (even if the number of terms is set).
 * If the number of terms is not set, returns just the first elected date.
 * If the number of terms is 0, returns "${firstElectedDate}, First Term".
 * If the number of terms is 1, returns "${firstElectedDate}, 1 term".
 * Otherwise, returns "${firstElectedDate}, ${numberOfTerms} terms".
 */
const firstElectedWithTenureText = computed(() => {
  let firstElected = profile.value?.firstElected.trim() ?? "";
  const tenure = profile.value?.numberTenuresInSeat ?? 0;
  const getTenureString = (tenure: number): string => {
    if (!tenure) return "";
    if (tenure === 1) return "First Term";
    return tenure + " terms";
  };
  if (!!firstElected && !!tenure) firstElected += ",";
  return `${firstElected} ${getTenureString(tenure)}`.trim();
});

const showInBrief = computed(() => {
  if (!profile.value) return false;
  return (
    profile.value.phoneticName ||
    profile.value.birthDate ||
    profile.value.birthplace ||
    profile.value.hometown ||
    profile.value.residences.length > 0 ||
    profile.value.religion ||
    profile.value.ethnicity ||
    profile.value.education.length > 0 ||
    profile.value.educationSummary ||
    profile.value.maritalStatus ||
    profile.value.familyMembers ||
    profile.value.title ||
    profile.value.politicalParty ||
    profile.value.electoralHistory.length > 0 ||
    profile.value.workHistory ||
    profile.value.electoralOverview
  );
});
// </editor-fold>

// <editor-fold desc="UI Properties">
const display404 = ref(false);
const display500 = ref(false);
const docxBtnClicked = ref(false);
const pdfBtnClicked = ref(false);
const docxWithCustomLogoBtnClicked = ref(false);
const requestCustomLogoDialog = ref(false);
const panelsToBeOpened = ref<string[]>([
  "Quick Summary",
  "Congressional Information",
  "Legislative Information",
  "Congressional Staff",
  "Legislative Staff",
  "Gubernatorial Staff",
  "Web and Social Media",
  "Office Contact Information",
]);

// </editor-fold>

const pageName = ref("");
const { pageTitle, setAnalyticsMetadata, pushPageViewEvent } =
  useViewAnalytics(pageName);

const congressionalStaffersHeaders = ref<DataTableHeader[]>([
  {
    text: "Name",
    value: "stafferFullName",
    sortable: true,
    filterable: true,
    align: "start",
    sort: (a: string, b: string) => {
      return a.localeCompare(b);
    },
  },
  {
    text: "Job Categories",
    value: "rolenames",
    sortable: true,
    filterable: true,
    align: "start",
  },
]);

const focusAreaHeader: DataTableHeader = {
  text: "Focus Areas",
  value: "stafferInterests",
  sortable: true,
  filterable: true,
  align: "start",
};
const stafferSearch = ref("");

const formatCurrency = (value: number): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "(Unavailable)";
  }
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

const hierarchyChips = computed<Chip[]>(() => profile.value?.chips ?? []);

const clickPdfBtn = (): void => {
  pdfBtnClicked.value = true;
  setTimeout(() => {
    pdfBtnClicked.value = false;
  }, 4000);
};

const clickDocxBtn = (): void => {
  docxBtnClicked.value = true;
  setTimeout(() => {
    docxBtnClicked.value = false;
  }, 4000);
};

const requestCustomLogo = (): void => {
  ApiService.postUserContact("", true).then((response) => {
    if (response.statusCode === 200) {
      LogService.info(`Profile.requestCustomLogo is successful.`);
      SnackbarService.displaySnackbar(
        "Request for custom logo successfully submitted.",
        "primary"
      );
    } else {
      LogService.error(
        `Profile.requestCustomLogo failed: '${response.error_message}'`
      );
      SnackbarService.displaySnackbar("An error occurred, please try again.");
    }
  });

  requestCustomLogoDialog.value = false;
};

const getAllCmsTags = (): string => {
  let result = "";

  if (profile.value != null) {
    if (!!profile.value.primaryProfileType) {
      result += profile.value.primaryProfileType;
    }
    if (profile.value.primaryProfileTypesAdditional.length > 0) {
      if (result.length > 0) result += ",";
      result += profile.value.primaryProfileTypesAdditional.join(",");
    }
    if (profile.value.secondaryProfileTypes.length > 0) {
      if (result.length > 0) result += ",";
      result += profile.value.secondaryProfileTypes.join(",");
    }
    if (profile.value.subscriptionModules.length > 0) {
      if (result.length > 0) result += ",";
      result += profile.value.subscriptionModules
        .map((module) => appStore.getSubscriptionModuleName(module))
        .join(",");
    }
  }
  return result;
};

const contentString = computed<string>(() => {
  if (!profile.value?.id) return "";
  if (route.name === VignetteRoutes.ProfileKW) {
    return `knowwho-person-${profile.value.id}`;
  } else {
    return `profile-${profile.value.id}`;
  }
});
const setAnalyticsMetadataAndSendEvent = (): void => {
  setAnalyticsMetadata(MetadataKeys.SITESECTION, "AlmanacPlus");
  if (profile.value != null) {
    setAnalyticsMetadata(MetadataKeys.PAGE_TYPE, "Database");
    setAnalyticsMetadata(MetadataKeys.CONTENT_TITLE, pageName.value);
    setAnalyticsMetadata(MetadataKeys.CMS_CONTENT_ID, contentString.value);
    setAnalyticsMetadata(MetadataKeys.CMS_TAGS, getAllCmsTags());
    setAnalyticsMetadata(
      MetadataKeys.VIGNETTE_PRIMARY_PROFILE_TYPE,
      profile.value.primaryProfileType
    );
    pushPageViewEvent();
  }
};

const handleHeadshotError = () => {
  if (profile.value) {
    profile.value.headshot = "";
  }
};

onBeforeMount(async () => {
  const profileIdParam = route.params.id;
  // mostly a type guard for profileIdParam
  if (!profileIdParam) {
    await router.replace({ name: VignetteRoutes.NotFound });
    return;
  }
  const profileNameParam = route.params.name;
  LogService.debug(
    `Profile.vue: profileIdParam: ${profileIdParam}, profileNameParam: ${profileNameParam}`
  );

  const profileId = +profileIdParam;

  if (isNaN(profileId)) {
    LogService.warn(
      `Profile.vue: Invalid profileId is requested: '${profileIdParam}'`
    );
  } else {
    await ApiService.refreshBriefcase();
    const { data, error, ok } = await ApiService.getProfile(
      profileId,
      route.name === VignetteRoutes.ProfileKW
    );
    if (ok) {
      profile.value = data;
      if (profile.value != null) {
        pageName.value = profile.value.preferredNameDisplay
          ? profile.value.preferredNameDisplay
          : "";
      }
      const hasFocusAreas = profile.value?.staffers?.some(
        (staffer) => staffer.stafferInterests?.length > 0
      );
      if (hasFocusAreas) {
        congressionalStaffersHeaders.value.push(focusAreaHeader);
      }
      setAnalyticsMetadataAndSendEvent();
    } else {
      if (error instanceof RedirectResponse) {
        await router.replace(error.targetUrl);
        return;
      }
      const status = error?.code;
      LogService.error(`ProfileDetail.vue: Error retrieving profile: ${error}`);
      if (status && Number(status) >= 500) {
        display500.value = true;
      } else {
        display404.value = true;
      }
    }
    isLoaded.value = true;
  }
});

return { __sfc: true,route, router, appStore, sessionStore, hasCustomLogo, profile, isLoaded, modules, socialLinks, facebookLink, getTwitterLinkFromString, twitterLink, instagramLink, instagramHandle, youtubeLink, linkedInLink, websiteLink, presentationCenterLink, emailLink, docxUrl, docxUrlWithCustomLogo, pdfUrl, showElectoralHistory, firstElectedWithTenureText, showInBrief, display404, display500, docxBtnClicked, pdfBtnClicked, docxWithCustomLogoBtnClicked, requestCustomLogoDialog, panelsToBeOpened, pageName, pageTitle, setAnalyticsMetadata, pushPageViewEvent, congressionalStaffersHeaders, focusAreaHeader, stafferSearch, formatCurrency, hierarchyChips, clickPdfBtn, clickDocxBtn, requestCustomLogo, getAllCmsTags, contentString, setAnalyticsMetadataAndSendEvent, handleHeadshotError, twitter, AdditionalRoleOffice, AdditionalRolesRow, ArchivedIcon, BriefcaseDialog, Dialog, ExportButton, NjBtn, OfficeAddress, ProfileCard, ProfileItem, ProfilePanel, ProfileSubModuleCards, ProfileWrapper, VignetteHelpers, VignetteRoutes, DownloadWithCustomLogoBtn, ElectionStatus, DjangoRouterLink, HierarchyChip }
}

})